const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1'

// Free-tier backends sleep: first request can take 60s+ to wake them.
// 90s timeout + one automatic retry so a sleeping backend doesn't look broken.
async function fetchWithWakeup(url: string, options: RequestInit): Promise<Response> {
  const attempt = async (timeoutMs: number): Promise<Response> => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      return await fetch(url, { ...options, signal: ctrl.signal })
    } finally {
      clearTimeout(timer)
    }
  }
  try {
    return await attempt(90000)
  } catch (e: any) {
    if (e?.name !== 'AbortError') throw e
    // Possibly waking up — wait, then try once more.
    await new Promise((r) => setTimeout(r, 15000))
    try {
      return await attempt(90000)
    } catch (e2: any) {
      throw new Error('Server se connect nahi ho paya (timeout). Backend jagao: /health kholo, 1 min ruko, phir Retry dabao.')
    }
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token')
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  }

  let res: Response
  try {
    res = await fetchWithWakeup(`${API_BASE}${path}`, { ...options, headers })
  } catch (e: any) {
    throw new Error(e.message || 'Server se connect nahi ho paya. Backend Live hai? /health check karo.')
  }

  if (res.status === 401) {
    localStorage.removeItem('access_token')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }))
    // FastAPI 422 me detail ARRAY hota hai — readable banao (pehle [object Object] dikhta tha).
    let msg: string
    if (Array.isArray(err.detail)) {
      msg = err.detail.map((d: any) => {
        const loc = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : ''
        return loc ? `${loc}: ${d.msg}` : String(d.msg || d)
      }).join('; ')
    } else {
      msg = err.detail || `HTTP ${res.status}`
    }
    throw new Error(msg)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) => request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),
  patch: <T>(path: string, data: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Human-readable channel label: title + short id (never a bare UUID). */
export function channelName(
  channels: { id: string; title: string }[] | undefined,
  id: string | null | undefined,
): string {
  if (!id) return '-'
  return channels?.find((c) => c.id === id)?.title || `${id.slice(0, 8)}…`
}

// Types
export interface User {
  id: string
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  updated_at: string
  last_login_at: string | null
}

export interface Channel {
  id: string
  owner_id: string
  youtube_channel_id: string
  title: string
  description: string | null
  custom_url: string | null
  country: string | null
  default_language: string
  made_for_kids: boolean
  niche: string
  priority: number
  /** 2.2.34: ON = compliance ke baad upload rukta hai (preview → Approve/Reject) */
  review_required: boolean
  /** 2.2.43: channel ki aawaz/style — script prompt me inject hoti hai */
  voice_profile?: string | null
  is_active: boolean
  ypp_eligible: boolean
  subscriber_count: number
  view_count: number
  video_count: number
  created_at: string
  updated_at: string
  last_synced_at: string | null
}

export interface Topic {
  id: string
  channel_id: string
  title: string
  description: string | null
  keywords: string | null
  category: string | null
  source: string | null
  source_url: string | null
  language: string
  target_countries: string | null
  status: string
  priority: number
  research_data: string | null
  created_at: string
  updated_at: string
  approved_at: string | null
}

export interface Video {
  id: string
  channel_id: string
  topic_id: string | null
  job_id: string | null
  youtube_video_id: string | null
  title: string
  description: string | null
  tags: string | null
  default_language: string
  default_audio_language: string
  category_id: string
  visibility: string
  status: string
  script: string | null
  script_language: string
  voice_audio_path: string | null
  voice_language: string
  voice_provider: string
  voice_id: string | null
  duration_seconds: number | null
  thumbnail_path: string | null
  video_file_path: string | null
  scheduled_at: string | null
  published_at: string | null
  created_at: string
  updated_at: string
  localizations: VideoLocalization[]
  policy_checks: VideoPolicyCheck[]
  analytics: VideoAnalytics[]
  seo_score: number
  seo_issues: string[]
}

export interface VideoLocalization {
  id: string
  video_id: string
  language: string
  title: string
  description: string | null
  is_auto_generated: boolean
  created_at: string
  updated_at: string
}

export interface VideoPolicyCheck {
  id: string
  video_id: string
  check_type: string
  severity: string
  passed: boolean
  message: string
  details: string | null
  auto_fixable: boolean
  fixed_at: string | null
  created_at: string
}

export interface VideoAnalytics {
  id: string
  video_id: string
  date: string
  views: number
  watch_time_minutes: number
  average_view_duration_seconds: number
  likes: number
  dislikes: number
  comments: number
  shares: number
  subscribers_gained: number
  subscribers_lost: number
  estimated_revenue_usd: number
  impressions: number
  ctr: number
  created_at: string
}

export interface Job {
  id: string
  channel_id: string
  topic_id: string | null
  video_id: string | null
  job_type: string
  status: string
  /** 2.2.43 job-pause: user ne ⏸ dabaya — stage-boundary par ruki job */
  paused: boolean
  current_stage: string
  progress_pct: number
  input_data: string | null
  output_data: string | null
  error_message: string | null
  retry_count: number
  max_retries: number
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  logs: JobLog[]
}

export interface JobLog {
  id: string
  job_id: string
  stage: string
  level: string
  message: string
  meta: string | null
  created_at: string
}

export interface AnalyticsSummary {
  video_id: string
  total_views: number
  total_watch_time_minutes: number
  total_likes: number
  total_comments: number
  total_shares: number
  net_subscribers: number
  estimated_revenue_usd: number
  avg_ctr: number
  period_start: string
  period_end: string
}

export interface ChannelAnalyticsSummary {
  channel_id: string
  total_views: number
  total_watch_time_minutes: number
  total_estimated_revenue_usd: number
  total_videos: number
  avg_ctr: number
  top_videos: AnalyticsSummary[]
}

export interface CountryStat {
  country: string
  title: string | null
  video_id: string | null
  views: number
  watch_time_minutes: number
  subscribers_gained: number
}

export interface BackupSyncReport {
  at: string | null
  ok: boolean | null
  note: string
  pulled?: number
  pushed_tables?: number
  error?: string
  active_db?: string
}

export interface DbQuota {
  used_bytes: number | null
  limit_bytes: number
  unit?: string
  note?: string
  /** Neon: CU-hours (used None = API se nahi milta / quota out) */
  compute_used_hours?: number | null
  compute_limit_hours?: number
}

export interface NeonStatus {
  db_ok: boolean
  latency_ms: number | null
  cold_wake: boolean
  role?: string
  allowance: string
  resets_on: string
  console_url: string
  live_usage: null
  live_usage_note: string
  quota?: DbQuota
  backup_sync?: BackupSyncReport
}

/** 2.2.36: Global upload kill-switch (Task 3). PAUSED = koi bhi YouTube
 * upload nahi; "Start online uploading" button = resume + pending flush. */
export interface UploadStatus {
  paused: boolean
  pending_uploads: number
  version: string
}

export interface UploadStartResult {
  paused: boolean
  pending_uploads: number
  triggered: number
  note: string
}

export interface DbRetentionReport {
  ran_at?: string
  mode?: 'daily' | 'aggressive'
  pruned?: Record<string, number>
  total_rows?: number
  bytes_before?: number | null
  bytes_after?: number | null
  freed_bytes?: number | null
  vacuum?: string
  retention_days?: Record<string, number>
  error?: string
}

export interface QuotaGuardReport {
  checked_at?: string
  used_bytes?: number | null
  limit_bytes?: number
  used_pct?: number | null
  threshold_pct?: number
  action?: 'ok' | 'cooldown' | 'aggressive-prune'
  fired?: boolean
  error?: string
}

export interface SupabaseStatus {
  db_ok: boolean
  latency_ms: number | null
  cold_wake: boolean
  role: string
  host: string
  region: string
  allowance: string
  resets_on: string
  console_url: string
  live_usage: null
  live_usage_note: string
  quota?: DbQuota
  /** auto-retention ka last run (prune + VACUUM) */
  retention?: DbRetentionReport
  /** hourly quota guard (85%+ par aggressive prune) */
  guard?: QuotaGuardReport
  backup_sync?: BackupSyncReport
  failover_cause?: string
}

export interface PlatformService {
  id: 'render' | 'cloudflare' | 'github'
  name: string
  status_url: string
  console_url: string
  quota_label: string
  quota_limit: string
  quota_note: string
  /** asli usage jab mile (GitHub rate-limit API), warna null */
  quota_used: number | null
  quota_limit_num: number | null
  quota_unit: string
  /** statuspage indicator: none | minor | major | maintenance | unknown */
  indicator: string
  description: string
  /** live detail: uptime / HTTP code / API remaining */
  detail: string
  checked_at: string
}

export interface PlatformStatus {
  services: PlatformService[]
  cached_ttl_sec?: number
}

export interface PolicyCheckResult {
  video_id: string
  overall_passed: boolean
  blocker_count: number
  error_count: number
  warning_count: number
  info_count: number
  checks: PolicyCheckDetail[]
}

export interface PolicyCheckDetail {
  check_type: string
  severity: string
  passed: boolean
  message: string
  details: string | null
  auto_fixable: boolean
}